import useSearchAlertsService, {
  SearchAlert,
} from 'api/services/searchAlertsService'
import Loading from 'components/common/loading/Loading'
import {
  saveFiltersForRestore,
} from 'components/listings/listingsEmptyHelpers'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Filters } from 'types/general'

type MySpaceAlertsSectionProps = {
  alerts: SearchAlert[] | undefined
  isLoading: boolean
  onChanged: () => void
}

const BellIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
)

function statusBadgeClass(status: SearchAlert['status']): string {
  if (status === 'active') return 'bg-green-100 text-green-800'
  if (status === 'paused') return 'bg-amber-100 text-amber-800'
  return 'bg-gray-100 text-gray-700'
}

const MySpaceAlertsSection = forwardRef<HTMLElement, MySpaceAlertsSectionProps>(
  ({ alerts, isLoading, onChanged }, ref) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { updateAlert, deleteAlert } = useSearchAlertsService()
    const [busyId, setBusyId] = useState<number | null>(null)
    const items = alerts ?? []

    const runAction = async (id: number, action: () => Promise<boolean | SearchAlert | null>) => {
      setBusyId(id)
      try {
        const result = await action()
        if (!result) {
          toast.error(String(t('account.mySpace.alertsActionFailed')))
          return
        }
        onChanged()
      } finally {
        setBusyId(null)
      }
    }

    const handleTogglePause = (alert: SearchAlert) => {
      if (alert.status === 'pending_confirmation') {
        toast.info(String(t('account.mySpace.alertsPendingConfirm')))
        return
      }
      const nextStatus = alert.status === 'active' ? 'paused' : 'active'
      void runAction(alert.id, async () => {
        const updated = await updateAlert({ id: alert.id, status: nextStatus })
        if (updated) {
          toast.success(
            String(
              nextStatus === 'paused'
                ? t('account.mySpace.alertsPaused')
                : t('account.mySpace.alertsResumed')
            )
          )
        }
        return updated
      })
    }

    const handleEdit = (alert: SearchAlert) => {
      if (alert.criteria && typeof alert.criteria === 'object') {
        saveFiltersForRestore(alert.criteria as Filters)
      }
      navigate('/listings')
    }

    const handleDelete = (alert: SearchAlert) => {
      if (!window.confirm(String(t('account.mySpace.alertsDeleteConfirm')))) return
      void runAction(alert.id, async () => {
        const ok = await deleteAlert(alert.id)
        if (ok) toast.success(String(t('account.mySpace.alertsDeleted')))
        return ok
      })
    }

    return (
      <section
        ref={ref}
        id="my-space-alerts"
        className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
            <BellIcon />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-imovSecondary">
              {t('account.mySpace.alertsTitle')}
            </h2>
            <p className="text-sm text-gray-500">{t('account.mySpace.alertsSubtitle')}</p>
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
            <p className="text-sm text-gray-600">{t('account.mySpace.noAlerts')}</p>
            <button
              type="button"
              onClick={() => navigate('/listings')}
              className="mt-3 inline-flex rounded-xl bg-imovPrimary px-4 py-2 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
            >
              {t('account.mySpace.browseListings')}
            </button>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map(alert => {
              const busy = busyId === alert.id
              const summary =
                alert.summaryLabel?.trim() ||
                t('account.mySpace.alertsUntitled')

              return (
                <li key={alert.id} className="rounded-xl bg-gray-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-imovSecondary">{summary}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(
                            alert.status
                          )}`}
                        >
                          {t(`account.mySpace.alertsStatus.${alert.status}`)}
                        </span>
                        {alert.newMatchesCount > 0 ? (
                          <span className="inline-flex rounded-full bg-imovPrimary/10 px-2.5 py-0.5 text-xs font-semibold text-imovPrimary">
                            {t('account.mySpace.alertsNewMatches', {
                              count: alert.newMatchesCount,
                            })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleEdit(alert)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-imovSecondary hover:bg-gray-50 disabled:opacity-50"
                      >
                        {t('account.mySpace.alertsEdit')}
                      </button>
                      <button
                        type="button"
                        disabled={busy || alert.status === 'pending_confirmation'}
                        onClick={() => handleTogglePause(alert)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-imovSecondary hover:bg-gray-50 disabled:opacity-50"
                      >
                        {alert.status === 'paused'
                          ? t('account.mySpace.alertsResume')
                          : t('account.mySpace.alertsPause')}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleDelete(alert)}
                        className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        {t('account.mySpace.alertsDelete')}
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    )
  }
)

MySpaceAlertsSection.displayName = 'MySpaceAlertsSection'

export default MySpaceAlertsSection
